import { useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

export const useOpenAlex = () => {
  const { language } = useLanguage();

  // --- STATE FÖR FÖRFATTARE ---
  const [authorQuery, setAuthorQuery] = useState('');
  const [authorSuggestions, setAuthorSuggestions] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [isSearchingAuthors, setIsSearchingAuthors] = useState(false);

  // --- STATE FÖR TIDSKRIFT ---
  const [journalQuery, setJournalQuery] = useState('');
  const [journalSuggestions, setJournalSuggestions] = useState([]);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [isSearchingJournals, setIsSearchingJournals] = useState(false);

  // --- STATE FÖR LÄROSÄTE / SJUKHUS / ORGANISATION ---
  const [institutionQuery, setInstitutionQuery] = useState('');
  const [institutionSuggestions, setInstitutionSuggestions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [isSearchingInstitutions, setIsSearchingInstitutions] = useState(false);

  // --- STATE FÖR UNDERINSTITUTION / AVDELNING (t.ex. "Molekylär medicin" eller "Kemi") ---
  const [departmentQuery, setDepartmentQuery] = useState('');

  // --- SÖKFUNKTIONER ---
  const searchForAuthor = async () => {
    if (!authorQuery.trim()) return;
    setIsSearchingAuthors(true);
    setAuthorSuggestions([]);
    setSelectedAuthor(null);

    try {
      const response = await fetch(
        `https://api.openalex.org/authors?search=${encodeURIComponent(authorQuery)}&per_page=10`
      );
      const data = await response.json();
      if (data.results) {
        setAuthorSuggestions(
          data.results.map((author) => ({
            id: author.id.split('/').pop(),
            name: author.display_name,
            hint:
              author.hint ||
              author.last_known_institutions?.[0]?.display_name ||
              author.affiliations?.[0]?.institution?.display_name ||
              author.last_known_institution?.display_name ||
              t(language, 'openalex_no_institution'),
            works_count: author.works_count
          }))
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearchingAuthors(false);
    }
  };

  const selectAuthor = (author) => {
    setSelectedAuthor(author);
    setAuthorSuggestions([]);
    setAuthorQuery(author.name);
  };

  const clearAuthor = () => {
    setSelectedAuthor(null);
    setAuthorQuery('');
    setAuthorSuggestions([]);
  };

  const searchForJournal = async () => {
    if (!journalQuery.trim()) return;
    setIsSearchingJournals(true);
    setJournalSuggestions([]);
    setSelectedJournal(null);

    try {
      const response = await fetch(
        `https://api.openalex.org/sources?search=${encodeURIComponent(journalQuery)}&per_page=10`
      );
      const data = await response.json();
      if (data.results) {
        setJournalSuggestions(
          data.results.map((source) => ({
            id: source.id.split('/').pop(),
            name: source.display_name,
            hint: source.host_organization_name || t(language, 'openalex_no_publisher'),
            works_count: source.works_count
          }))
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearchingJournals(false);
    }
  };

  const selectJournal = (journal) => {
    setSelectedJournal(journal);
    setJournalSuggestions([]);
    setJournalQuery(journal.name);
  };

  const clearJournal = () => {
    setSelectedJournal(null);
    setJournalQuery('');
    setJournalSuggestions([]);
  };

  const searchForInstitution = async () => {
    if (!institutionQuery.trim()) return;
    setIsSearchingInstitutions(true);
    setInstitutionSuggestions([]);
    setSelectedInstitution(null);

    try {
      // Sortering efter works_count:desc gör att stora lärosäten prioriteras i förslagen
      const response = await fetch(
        `https://api.openalex.org/institutions?search=${encodeURIComponent(institutionQuery)}&sort=works_count:desc&per_page=10`
      );
      const data = await response.json();
      if (data.results) {
        setInstitutionSuggestions(
          data.results.map((institution) => {
            const locationParts = [
              institution.geo?.city,
              institution.geo?.country
            ].filter(Boolean);
            const locationStr = locationParts.length > 0 ? locationParts.join(', ') : institution.country_code;
            const typeStr = institution.type ? ` (${institution.type})` : '';

            return {
              id: institution.id.split('/').pop(),
              name: institution.display_name,
              hint: locationStr ? `${locationStr}${typeStr}` : (institution.hint || ''),
              country_code: institution.country_code,
              works_count: institution.works_count,
              ror: institution.ror
            };
          })
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSearchingInstitutions(false);
    }
  };

  const selectInstitution = (institution) => {
    setSelectedInstitution(institution);
    setInstitutionSuggestions([]);
    setInstitutionQuery(institution.name);
  };

  const clearInstitution = () => {
    setSelectedInstitution(null);
    setInstitutionQuery('');
    setInstitutionSuggestions([]);
    setDepartmentQuery('');
  };

  return {
    authorQuery,
    setAuthorQuery,
    authorSuggestions,
    setAuthorSuggestions,
    selectedAuthor,
    setSelectedAuthor,
    isSearchingAuthors,
    searchForAuthor,
    selectAuthor,
    clearAuthor,

    journalQuery,
    setJournalQuery,
    journalSuggestions,
    setJournalSuggestions,
    selectedJournal,
    setSelectedJournal,
    isSearchingJournals,
    searchForJournal,
    selectJournal,
    clearJournal,

    institutionQuery,
    setInstitutionQuery,
    institutionSuggestions,
    setInstitutionSuggestions,
    selectedInstitution,
    setSelectedInstitution,
    isSearchingInstitutions,
    searchForInstitution,
    selectInstitution,
    clearInstitution,

    departmentQuery,
    setDepartmentQuery
  };
};